/**
 * Logarithmic scale utilities for Space and Dissonance parameters
 *
 * Provides more precision at low values (0-10) with 0.5 increments,
 * then switches to integer precision for higher values (10-100).
 *
 * Uses a logarithmic mapping to give finer control near 0.
 */

/**
 * Convert linear slider value (0-100) to logarithmic display value
 *
 * Uses exponential scaling: value = 100 * (10^(linear/100) - 1) / 9
 * This gives more precision near 0 and less near 100.
 *
 * @param linear - Linear value from slider (0-100)
 * @returns Logarithmic value (0-100) with appropriate precision
 */
export function linearToLog(linear: number): number {
  if (linear < 0) return 0;
  if (linear > 100) return 100;

  // Exponential curve: maps 0-100 linear to 0-100 logarithmic
  const logValue = 100 * (Math.pow(10, linear / 100) - 1) / 9;

  // Round based on value range
  if (logValue <= 10) {
    // 0-10: Round to nearest 0.5
    return Math.round(logValue * 2) / 2;
  } else {
    // 10-100: Round to nearest integer
    return Math.round(logValue);
  }
}

/**
 * Convert logarithmic display value to linear slider value (0-100)
 *
 * Inverse of linearToLog: linear = 100 * log₁₀(1 + 9 * log/100)
 *
 * @param log - Logarithmic value (0-100)
 * @returns Linear slider value (0-100)
 */
export function logToLinear(log: number): number {
  if (log < 0) return 0;
  if (log > 100) return 100;

  // Inverse exponential: maps 0-100 logarithmic to 0-100 linear
  return 100 * Math.log10(1 + 9 * log / 100);
}

/**
 * Format logarithmic value for display with appropriate precision
 *
 * @param value - Logarithmic value (0-100)
 * @returns Formatted string with 1 decimal place (0-10) or integer (10-100)
 */
export function formatLogValue(value: number): string {
  if (value <= 10) {
    return value.toFixed(1);
  }
  return Math.round(value).toString();
}
